// AgentCricketPanel — the agent's CRICKET earnings page, /games/cricket.
//
// U6 rebuild (2026-07-09, owner correction): the U3 route-move + U5 i18n
// sweep were NOT enough — the page still wore its own neutral-* grays,
// desktop tables and hand-rolled cards. It now sits fully on the shared
// system per the doctrine (docs/AGENT_UI_SIMPLIFICATION_REPORT.md §5.2):
//   * back-bar to Games (same pattern as the Profile spokes)
//   * HeroCard (emerald) for the cricket balance
//   * T.* text tokens — nothing below /60 in dark
//   * standard card rows, mobile-first STACKED rows instead of 5- and
//     9-column tables (portrait phone is the primary device)
//   * arena/neon theme palette only — no raw neutral-*
//
// Data + behaviour unchanged: read-only from cricket's OWN isolated books
// via /admin/cricket-report/summary (self-scoped server-side), 60s poll,
// refetch on focus, inline per-player limits editor (server-enforced).

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '@baaligames/shared-ui/components/Icon';
import SportLimitsEditor from '@baaligames/shared-ui/admin/components/SportLimitsEditor';
import HeroCard from '@baaligames/shared-ui/admin/components/HeroCard';
import { T } from '@baaligames/shared-ui/admin/lib/textStyles';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { useT } from '@baaligames/shared-ui/lib/i18n';

type Summary = {
  agentId: number;
  cricketBalance: number;
  commissionTotal: number;
  commissionThisWeek: number;
  rates: { profitSharePct: number; commissionSharePct: number };
  returnPct?: number | null;
  byPillar: { kind: string; net: number; count: number }[];
  weekly: { weekStart: string; net: number; count: number }[];
  recent: { id: number; kind: string; amount: number; sourcePlayer: number | null; ratePct: number | null; at: string }[];
  players: { id: number; username: string; balance: number; weekTurnover: number; openAtRisk: number; earnedFrom: number }[];
  subAgents: { id: number; username: string; cricketBalance: number; profitSharePct: number; commissionSharePct: number; players: number; myResidualFromThem: number; theirOwnEarned: number }[];
  upline: { id: number; username: string } | null;
};

const inr = (n: number) => `₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;
const signed = (n: number) => (n < 0 ? `−${inr(n)}` : `+${inr(n)}`);
// A BALANCE keeps its sign. inr() strips it (correct inside signed(), which re-adds
// one), so the hero used to render a NEGATIVE cricket wallet as positive — a broker
// down ₹1,054 saw "CRICKET BALANCE ₹1,054" while the This-week/All-time figures
// right beneath it correctly read −₹1,054. No leading '+' — it's a balance.
const inrBalance = (n: number) => (n < 0 ? `−${inr(n)}` : inr(n));
const moneyTone = (n: number) =>
  n > 0 ? 'text-emerald-600 dark:text-emerald-300'
  : n < 0 ? 'text-rose-600 dark:text-rose-300'
  : 'text-arena-500 dark:text-neon-200/60';

// Translation keys — components pass PILLAR_LABEL[k] through t().
const PILLAR_LABEL: Record<string, string> = {
  profit_share: 'agent.cricketPage.pillar.profitShare',
  commission_share: 'agent.cricketPage.pillar.commShare',
  turnover_share: 'agent.hisaab.pillar.short.turnover_share',
  adjust: 'agent.cricketPage.pillar.adjust',
};

// ── Shared shells (standard card language from the Games hub) ────────

function SectionLabel({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div className={`flex items-center gap-1.5 px-1 pb-1.5 text-[11px] font-extrabold uppercase tracking-wider ${T.secondary}`}>
      <Icon name={icon} size={14} className="text-emerald-600 dark:text-emerald-300" />
      {children}
    </div>
  );
}

function SectionCard({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl bg-white dark:bg-arena-700/40
                     border border-arena-200/60 dark:border-arena-600/40
                     shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none ${className}`}>
      {children}
    </div>
  );
}

function RateChip({ label, pct }: { label: string; pct: number }) {
  const on = pct > 0;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold
                      ${on
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                        : `bg-arena-100/70 dark:bg-white/[0.07] ${T.tertiary}`}`}>
      {label} <b className="font-black">{pct}%</b>
    </span>
  );
}

export default function AgentCricketPanel() {
  const t = useT();
  const [data, setData] = useState<Summary | null>(null);
  const [err, setErr] = useState('');
  const [limitsFor, setLimitsFor] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get<Summary>('/admin/cricket-report/summary');
      setData(r);
      setErr('');
    } catch (e: any) { setErr(e?.message || t('agent.cricketPage.error.loadFailed')); }
  }, []);
  useEffect(() => {
    void load();
    const iv = setInterval(load, 60_000);
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(iv); window.removeEventListener('focus', onFocus); };
  }, [load]);

  const pillar = (k: string) => data?.byPillar.find((p) => p.kind === k);

  return (
    <div className="max-w-xl lg:max-w-3xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4 pb-24">
      {/* Back bar — same pattern as the Profile spokes: one tap back to
          the Games hub, current page named right next to it. */}
      <div className="px-2 lg:px-4 flex items-center gap-2">
        <Link
          to="/games"
          className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5
                      text-[13px] font-bold ${T.secondary}
                      bg-arena-100/70 dark:bg-white/[0.07]
                      ring-1 ring-inset ring-arena-200/60 dark:ring-white/10
                      hover:bg-arena-100 dark:hover:bg-white/10 transition`}
        >
          <Icon name="arrow_back" size={16} />
          {t('agent.games.hub.title')}
        </Link>
        <span className={`text-[15px] font-display font-semibold ${T.primary}`}>
          {t('agent.cricketPage.title')}
        </span>
      </div>

      <div className="px-2 lg:px-4 space-y-4">
        {err && (
          <div className="rounded-2xl bg-rose-500/10 border border-rose-400/30 text-rose-700 dark:text-rose-200 px-4 py-3 text-sm">
            {err}
          </div>
        )}

        {!err && !data && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-24 rounded-2xl bg-arena-100/70 dark:bg-white/5 animate-pulse" />
            ))}
          </div>
        )}

        {data && (
          <>
            {/* Hero — the one number: my cricket balance. This-week and
                all-time sit inside as two mini figures. */}
            <HeroCard
              tone="emerald" icon="sports_cricket"
              kicker={t('agent.cricketPage.stat.balance')}
              value={inrBalance(data.cricketBalance)}
              sub={<>
                {t('agent.cricketPage.subtitle')}{' '}
                {data.upline
                  ? <>{t('agent.cricketPage.upline')} <b className="text-white">{data.upline.username}</b>.</>
                  : t('agent.cricketPage.uplineHouse')}
              </>}
            >
              <div className="mt-3 pt-3 border-t border-white/20 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-white/80">{t('agent.cricketPage.stat.week')}</div>
                  <div className="font-black text-[20px] tabular-nums">{signed(data.commissionThisWeek)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-white/80">{t('agent.cricketPage.stat.allTime')}</div>
                  <div className="font-black text-[20px] tabular-nums">{signed(data.commissionTotal)}</div>
                </div>
              </div>
            </HeroCard>

            {/* My rates — the same chips, on theme tokens. */}
            <div className="flex flex-wrap gap-2 px-0.5">
              <RateChip label={t('agent.cricketPage.chip.pl')} pct={data.rates.profitSharePct} />
              <RateChip label={t('agent.cricketPage.chip.comm')} pct={data.rates.commissionSharePct} />
            </div>

            {/* Where it comes from — pillar cards. The turnover RETURN card
                only appears when a return exists, and its amount is ORANGE
                (owner 2026-07-10): it is the extra house-paid stream, never
                to be confused with the P&L partnership money. */}
            <div>
              <SectionLabel icon="account_tree">{t('agent.cricketPage.pillars.title')}</SectionLabel>
              <div className="grid grid-cols-2 gap-3">
                {(['profit_share', 'commission_share', 'turnover_share'] as const)
                  .filter((k) => k !== 'turnover_share' || (pillar(k)?.net ?? 0) !== 0)
                  .map((k) => {
                    const p = pillar(k);
                    const isReturn = k === 'turnover_share';
                    const hint = k === 'profit_share' ? t('agent.cricketPage.hint.profitShare')
                      : isReturn ? t('agent.cricketPage.hint.turnoverReturn')
                      : t('agent.cricketPage.hint.commShare');
                    return (
                      <SectionCard key={k} className="p-3.5">
                        <div className={`text-[12px] font-bold ${isReturn ? 'text-orange-600 dark:text-orange-400' : T.secondary}`}>
                          {t(PILLAR_LABEL[k])}{isReturn && data.returnPct != null ? `[${data.returnPct}%]` : ''}
                        </div>
                        <div className={`font-black text-[20px] tabular-nums mt-0.5 ${isReturn ? 'text-orange-600 dark:text-orange-400' : moneyTone(p?.net ?? 0)}`}>{signed(p?.net ?? 0)}</div>
                        <div className={`text-[11px] leading-snug mt-1 ${T.faint}`}>
                          {t('agent.cricketPage.entries', { n: p?.count ?? 0 })} · {hint}
                        </div>
                      </SectionCard>
                    );
                  })}
              </div>
            </div>

            {/* My players — stacked rows (mobile-first), limits inline. */}
            <div>
              <SectionLabel icon="group">{t('agent.cricketPage.players.title')}</SectionLabel>
              <SectionCard>
                {data.players.length === 0 ? (
                  <div className={`text-sm py-6 text-center ${T.tertiary}`}>{t('agent.cricketPage.players.empty')}</div>
                ) : (
                  <div className="divide-y divide-arena-100 dark:divide-white/8">
                    {data.players.map((p) => (
                      <div key={p.id} className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <div className={`text-[14px] font-bold ${T.primary}`}>
                              {p.username} <span className={`font-medium ${T.faint}`}>#{p.id}</span>
                            </div>
                            <div className={`text-[11.5px] mt-0.5 ${T.tertiary}`}>
                              {t('agent.cricketPage.players.col.turnover')} <b className="tabular-nums">{inr(p.weekTurnover)}</b>
                              {' · '}{t('agent.cricketPage.players.col.atRisk')} <b className="tabular-nums text-amber-600 dark:text-amber-300">{inr(p.openAtRisk)}</b>
                              {' · '}{t('agent.cricketPage.players.col.balance')} <b className="tabular-nums">{inr(p.balance)}</b>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`font-black text-[17px] tabular-nums ${moneyTone(p.earnedFrom)}`}>{signed(p.earnedFrom)}</div>
                            <div className={`text-[10px] ${T.faint}`}>{t('agent.cricketPage.players.col.earnedMe')}</div>
                          </div>
                          <button
                            onClick={() => setLimitsFor(limitsFor === p.id ? null : p.id)}
                            className={`shrink-0 min-h-[34px] px-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition
                                        ${limitsFor === p.id
                                          ? 'bg-brand-500 text-white'
                                          : 'bg-brand-50 text-brand-700 dark:bg-brand-400/15 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-400/25'}`}
                          >
                            {limitsFor === p.id ? t('common.close') : t('agent.cricketPage.limits.open')}
                          </button>
                        </div>
                        {limitsFor === p.id && <SportLimitsEditor userId={p.id} />}
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>

            {/* My downline — stacked rows: their rates + what I keep. */}
            {data.subAgents.length > 0 && (
              <div>
                <SectionLabel icon="support_agent">{t('agent.cricketPage.subs.title')}</SectionLabel>
                <SectionCard>
                  <div className="divide-y divide-arena-100 dark:divide-white/8">
                    {data.subAgents.map((a) => (
                      <div key={a.id} className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="min-w-0 flex-1">
                            <div className={`text-[14px] font-bold ${T.primary}`}>
                              {a.username} <span className={`font-medium ${T.faint}`}>#{a.id}</span>
                              <span className={`ml-2 text-[11px] font-semibold ${T.tertiary}`}>
                                {a.players} {t('agent.cricketPage.subs.col.players')}
                              </span>
                            </div>
                            <div className={`text-[11.5px] mt-0.5 ${T.tertiary}`}>
                              {t('agent.cricketPage.chip.pl')} <b>{a.profitSharePct}%</b>
                              {' · '}{t('agent.cricketPage.chip.comm')} <b>{a.commissionSharePct}%</b>
                            </div>
                            <div className={`text-[11.5px] mt-0.5 ${T.tertiary}`}>
                              {t('agent.cricketPage.subs.col.theyEarned')} <b className={`tabular-nums ${moneyTone(a.theirOwnEarned)}`}>{signed(a.theirOwnEarned)}</b>
                              {' · '}{t('agent.cricketPage.subs.col.balance')} <b className={`tabular-nums ${moneyTone(a.cricketBalance)}`}>{signed(a.cricketBalance)}</b>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className={`font-black text-[17px] tabular-nums ${moneyTone(a.myResidualFromThem)}`}>{signed(a.myResidualFromThem)}</div>
                            <div className={`text-[10px] ${T.faint}`}>{t('agent.cricketPage.subs.col.residual')}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className={`text-[11px] px-4 pb-3 pt-1 ${T.faint}`}>{t('agent.cricketPage.subs.note')}</p>
                </SectionCard>
              </div>
            )}

            {/* Weekly statement. */}
            <div>
              <SectionLabel icon="receipt_long">{t('agent.cricketPage.weekly.title')}</SectionLabel>
              <SectionCard>
                {data.weekly.length === 0 ? (
                  <div className={`text-sm py-6 text-center ${T.tertiary}`}>{t('agent.cricketPage.weekly.empty')}</div>
                ) : (
                  <div className="divide-y divide-arena-100 dark:divide-white/8">
                    {data.weekly.map((w, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-4 py-3">
                        <span className={`text-[13px] font-bold ${T.secondary}`}>
                          {t('agent.cricketPage.weekly.weekOf', { date: new Date(w.weekStart).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) })}
                        </span>
                        <span className={`text-[11px] ${T.faint}`}>{t('agent.cricketPage.entries', { n: w.count })}</span>
                        <span className={`font-black text-[15px] tabular-nums ${moneyTone(w.net)}`}>{signed(w.net)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>

            {/* Recent entries. */}
            <div>
              <SectionLabel icon="history">{t('agent.cricketPage.recent.title')}</SectionLabel>
              <SectionCard>
                {data.recent.length === 0 ? (
                  <div className={`text-sm py-6 text-center ${T.tertiary}`}>{t('agent.cricketPage.recent.empty')}</div>
                ) : (
                  <div className="divide-y divide-arena-100 dark:divide-white/8">
                    {data.recent.map((x) => (
                      <div key={x.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                        <div className="min-w-0 flex-1">
                          <div className={`text-[12.5px] font-bold truncate ${T.secondary}`}>
                            {PILLAR_LABEL[x.kind] ? t(PILLAR_LABEL[x.kind]) : x.kind}{x.ratePct != null ? ` @ ${x.ratePct}%` : ''}
                          </div>
                          <div className={`text-[11px] truncate ${T.faint}`}>
                            {x.sourcePlayer ? <>{t('agent.cricketPage.recent.player', { id: x.sourcePlayer })} · </> : null}
                            {new Date(x.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <span className={`shrink-0 font-black text-[14px] tabular-nums ${moneyTone(x.amount)}`}>{signed(x.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
